import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import CustomConfirm from "./CustomConfirm";

interface StationDeparturesProps {
  station: string | undefined;
  setStation: React.Dispatch<React.SetStateAction<string | undefined>>;
}

interface Train {
  vaihtoasema: string;
  trainNumber: number;
  trainType: string;
  commuterLineID: string;
  destinationStation: string;
  commercialTrack: string;
  cancelled: boolean;
  scheduledTime: string;
  liveEstimateTime: string;
  actualTime: string;
}

const StationDepartures: React.FC = () => {
  const [newStation, setNewStation] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const [vaihtojunatHaettu, setvaihtojunatHaettu] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showMore, setShowMore] = useState<boolean>(false);
  const [stationTrains, setStationTrains] = useState<{
    [key: string]: Train[];
  }>({});
  const [previousStations, setPreviousStations] = useState<string[]>([]);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [moveToTrain, setMoveToTrain] = useState("");
  const [moveToTrainNumber, setMoveToTrainNumber] = useState<number | "">("");

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    setYear(currentYear);
  }, []);

  const navigate = useNavigate();

  useEffect(() => {
    if (newStation !== "") {
      // Tarkistetaan ensin, onko newStation jo lyhenne
      const isShortCode = Object.keys(stationNames).some(
        (key) => key.toLowerCase() === newStation.toLowerCase(),
      );

      // Jos newStation on jo lyhenne, käytetään sitä isoilla kirjaimilla
      const stationToUse = isShortCode
        ? newStation.toUpperCase()
        : Object.keys(stationNames).find(
            (key) =>
              stationNames[key].toLowerCase() === newStation.toLowerCase(),
          ) || newStation; // Käytetään löytynyttä lyhennettä tai alkuperäistä arvoa

      if (stationNames[stationToUse]) {
        setNewStation(stationToUse);
        fetchStationPassingTrains(stationToUse);

        if (scrollRef.current) {
          scrollRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }
    }
  }, [newStation]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [loading]);

  const fetchStationPassingTrains = async (stationShortCode: string) => {
    setShowMore(false);
    setLoading(true);
    try {
      const response = await fetch(
        `https://rata.digitraffic.fi/api/v1/live-trains/station/${stationShortCode}?minutes_before_departure=1440&minutes_after_departure=0&minutes_before_arrival=0&minutes_after_arrival=0&train_categories=Commuter,Long-distance`,
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      } else {
        console.log("fetchStationPassingTrains");
        saveLastStations(stationShortCode);
      }
      const data = await response.json();

      const startTime = Date.now();
      const endTime = startTime + 24 * 60 * 60 * 1000; // 24 tuntia myöhemmin, sijoitettu myös APIin minutes_before_departure=1440

      // Suodatetaan data
      const filteredTrains = data.flatMap((train: any) => {
        // Etsitään viimeinen ARRIVAL asema (määränpää)
        const lastArrivalStation = train.timeTableRows
          .filter((row: any) => row.type === "ARRIVAL")
          .pop(); // Hakee viimeisen ARRIVAL-tyypin rivin

        // Jos lastArrivalStation löytyy, lisätään se määränpääksi
        const destinationStation = lastArrivalStation
          ? lastArrivalStation.stationShortCode
          : "Unknown"; // Jos määränpäätä ei löydy, laita "Unknown"

        return train.timeTableRows
          .filter(
            (row: any) =>
              row.stationShortCode === stationShortCode &&
              row.type === "DEPARTURE" &&
              new Date(row.scheduledTime).getTime() > startTime &&
              new Date(row.scheduledTime).getTime() <= endTime &&
              train.trainType !== "HV",
          )
          .map((row: any) => ({
            vaihtoasema: stationShortCode,
            trainNumber: train.trainNumber, // Junan numero
            trainType: train.trainType,
            commuterLineID: train.commuterLineID,
            destinationStation, // Määränpää
            commercialTrack: row.commercialTrack,
            cancelled: row.cancelled,
            scheduledTime: row.scheduledTime,
            liveEstimateTime: row.liveEstimateTime,
            actualTime: row.actualTime,
          }));
      });

      setStationTrains((prevTrains) => ({
        ...prevTrains,
        [stationShortCode]: filteredTrains, // Liitetään junatiedot oikealle asemalle
      }));
    } catch (err) {
      console.error(
        err instanceof Error ? err.message : "Error fetching train data",
      );
    } finally {
      setLoading(false);
    }
    setvaihtojunatHaettu(true);
  };

  const handleClickTrain = (
    trainType: string,
    trainNumber: number,
    destinationStation: string,
  ) => {
    setMoveToTrainNumber(trainNumber);
    setMoveToTrain(
      `${trainType} ${trainNumber} ${stationNames[destinationStation]}`,
    );
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    navigate(`/${moveToTrainNumber}`);
  };

  const handleCancel = () => {
    setShowConfirm(false);
    console.log("stay");
  };

  const handleInputStation = (stationName: string) => {
    if (timer) {
      clearTimeout(timer);
    }
    const newTimer = setTimeout(() => {
      const cleanValue = DOMPurify.sanitize(stationName);
      setNewStation(cleanValue);
    }, 1200); // Viive
    setTimer(newTimer);
  };

  function saveLastStations(stationShortCode: string) {
    const cookies = localStorage.getItem("cookieConsent");
    if (cookies) {
      const consentData = JSON.parse(cookies);
      if (consentData.personalization === true) {
        const lastStationsString = localStorage.getItem("lastStations");
        let lastStations: string[] = [];

        if (lastStationsString) {
          lastStations = JSON.parse(lastStationsString);
        }
        const stationLongName =
          stationNames[stationShortCode] || stationShortCode;
        lastStations = lastStations.filter(
          (station) => station !== stationNames[stationShortCode],
        );

        lastStations.push(stationLongName);

        if (lastStations.length > 5) {
          lastStations.shift(); // Poistetaan vanhin asema
        }
        localStorage.setItem("lastStations", JSON.stringify(lastStations));
      }
    }
  }

  useEffect(() => {
    const lastStationsString = localStorage.getItem("lastStations");
    if (lastStationsString) {
      const lastStations: string[] = JSON.parse(lastStationsString);
      setPreviousStations(lastStations.reverse());
    }
  }, [newStation]);

  const stationNames: { [key: string]: string } = {
    AHO: "Ahonpää",
    AHV: "Ahvenus",
    AIN: "Ainola",
    ARL: "Airaksela",
    ATL: "Aittaluoto",
    AJO: "Ajos",
    APT: "Alapitkä",
    ALV: "Alavus",
    ALH: "Alholma",
    ARO: "Arola",
    ASO: "Asola",
    AVP: "Aviapolis",
    BSL: "Buslovskaja",
    DRA: "Dragsvik",
    DMV: "Dynamiittivaihde",
    EPZ: "Eläinpuisto-Zoo",
    ENO: "Eno",
    ERV: "Ervelä",
    ELA: "Eskola",
    EPO: "Espoo",
    HPJ: "Haapajärvi",
    HPS: "Haapakoski",
    HPK: "Haapamäki",
    HPA: "Haaparanta",
    HAA: "Haarajoki",
    HLT: "Hakosilta",
    HSI: "Haksi",
    HMA: "Hamina",
    HSL: "Hammaslahti",
    HNA: "Hanala",
    HGS: "Hangonsaari",
    HNH: "Hanhikoski",
    HKS: "Hankasalmi",
    HNK: "Hanko",
    HNKT: "Hanko tavara",
    HKP: "Hanko-Pohjoinen",
    HVA: "Harjavalta",
    HJ: "Harju",
    HRV: "Harviala",
    HD: "Haukipudas",
    HAU: "Haukivuori",
    HAS: "Hausjärvi tavara",
    HVS: "Haviseva",
    HVK: "Havukoski",
    HEK: "Heikkilä",
    HA: "Heinola",
    HNO: "Heinoo",
    HÄV: "Heinävaara",
    HNV: "Heinävesi",
    HKI: "Helsinki",
    KHK: "Helsinki Kivihaka",
    HNN: "Henna",
    HR: "Herrala",
    HKH: "Hiekkaharju",
    HIR: "Hiirola",
    HK: "Hikiä",
    HLS: "Hillosensalmi",
    HH: "Hinthaara",
    HVN: "Hirvineva",
    HP: "Humppila",
    HPL: "Huopalahti",
    HKO: "Huutokoski",
    HYR: "Hyrkäs",
    HYS: "Hyrynsalmi",
    HY: "Hyvinkää",
    PVL: "Hyvinkää Paavola",
    HYT: "Hyvinkää tavara",
    HL: "Hämeenlinna",
    HM: "Härmä",
    HÖL: "Höljäkkä",
    II: "Ii",
    ITR: "Iisalmen teollisuusraiteet",
    ILM: "Iisalmi",
    R983: "Iisalmi (R983)",
    ITA: "Iittala",
    ILA: "Ilmala",
    ILR: "Ilmala ratapiha",
    IOA: "Ilola",
    ILO: "Ilomantsi",
    IMR: "Imatra",
    IMT: "Imatra tavara",
    IMK: "Imatrankoski",
    IMKR: "Imatrankoski-raja",
    IM: "Immola",
    IN: "Inha",
    IKR: "Inkeroinen",
    IKO: "Inkoo",
    IKY: "Isokyrö",
    JAL: "Jalasjärvi",
    JPA: "Jepua",
    JNS: "Joensuu",
    KPP: "Joensuu Koppola",
    PLT: "Joensuu Peltola",
    SUL: "Joensuu Sulkulahti",
    JK: "Jokela",
    JOR: "Joroinen",
    JRS: "Jorvas",
    JTS: "Joutseno",
    JKI: "Juankoski",
    JUT: "Jutila",
    JJ: "Juupajoki",
    JRI: "Juurikorpi",
    JY: "Jyväskylä",
    JÄS: "Jämsä",
    JSK: "Jämsänkoski",
    JR: "Järvelä",
    JP: "Järvenpää",
    KPA: "Kaipiainen",
    KLA: "Kaipola",
    KJR: "Kaitjärvi",
    KAJ: "Kajaani",
    KTN: "Kaleton",
    KAU: "Kalkku",
    KAO: "Kalliovarasto",
    KSA: "Kalvitsa",
    KGS: "Kangas",
    KAN: "Kannelmäki",
    KSI: "Kannonkoski",
    KNS: "Kannus",
    KRR: "Karhejärvi",
    KHG: "Karhukangas",
    KR: "Karjaa",
    KRU: "Karkku",
    KAR: "Karviainen",
    KSK: "Kaskinen",
    KTH: "Kattilaharju",
    KJI: "Kauhajoki",
    KHA: "Kauhava",
    KLH: "Kauklahti",
    KLR: "Kaulinranta",
    KNI: "Kauniainen",
    KPL: "Kauppilanmäki",
    KA: "Kausala",
    KTP: "Keitelepohja",
    KEK: "Kekomäki",
    KEM: "Kemi",
    LI: "Kemi Lautiosaari",
    SHS: "Kemi Sahansaari",
    KJÄ: "Kemijärvi",
    KML: "Kempele",
    KEA: "Kera",
    R660: "Kerava (R660)",
    KE: "Kerava",
    KIÄ: "Kerimäki",
    KTI: "Kesälahti",
    KEU: "Keuruu",
    KIA: "Kiiala",
    KIL: "Kilo",
    KUA: "Kilpua",
    KNH: "Kinahmi",
    KII: "Kinni",
    KIJ: "Kirjola",
    KKN: "Kirkkonummi",
    KRN: "Kirkniemi",
    KIT: "Kitee",
    KN: "Kiukainen",
    KRV: "Kiuruvesi",
    KVJ: "Kivesjärvi",
    KIV: "Kivijärvi",
    KTÖ: "Kivistö",
    KOH: "Kohtavaara",
    KVU: "Koivu",
    KVH: "Koivuhovi",
    KVY: "Koivukylä",
    KKI: "Kokemäki",
    KOK: "Kokkola",
    KLI: "Kolari",
    KLO: "Kolho",
    KPI: "Kolppi",
    KMM: "Kommila",
    KOM: "Komu",
    KHI: "Kontiolahti",
    KON: "Kontiomäki",
    R934: "Kontiomäki (R934)",
    KRA: "Koria",
    KAS: "Korkeakoski",
    KRS: "Korso",
    KSU: "Korvensuo",
    KOS: "Koskenkorva",
    KTA: "Kotka",
    HOS: "Kotka Hovinsaari",
    MSS: "Kotka Mussalo",
    KTT: "Kotka tavara",
    KTS: "Kotkan satama",
    KOO: "Kotolahti",
    KV: "Kouvola",
    KVLA: "Kouvola lajittelu",
    OIK: "Kouvola Oikoraide",
    KVT: "Kouvola tavara",
    KOI: "Kovjoki",
    KPY: "Kruunupyy",
    KIS: "Kuivasjärvi",
    KUV: "Kullasvaara",
    KUO: "Kuopio",
    ILH: "Kuopio Iloharju",
    KUOT: "Kuopio tavara",
    KUT: "Kupittaa",
    KRM: "Kurkimäki",
    KU: "Kuurila",
    KUK: "Kuusankoski",
    KSN: "Kuusikkoniemi",
    KYN: "Kylänlahti",
    KY: "Kymi",
    KLN: "Kyminlinna",
    KÖ: "Kyrö",
    KYT: "Kytömaa",
    KLV: "Kälviä",
    KÄP: "Käpylä",
    KÖK: "Köykkäri",
    LAV: "Laajavuori",
    LPR: "Lahdenperä",
    LHN: "Lahnaslampi",
    LH: "Lahti",
    LAI: "Laihia",
    LAK: "Lakiala",
    LMK: "Lamminkoski",
    LAM: "Lamminniemi",
    LPJ: "Lapinjärvi",
    LNA: "Lapinlahti",
    LR: "Lappeenranta",
    LAA: "Lappila",
    LPO: "Lappohja",
    LPA: "Lapua",
    LYÖ: "Larvakytö",
    LAU: "Laukaa",
    LLA: "Laurila",
    LRS: "Lauritsala",
    LNÄ: "Leinelä",
    LKL: "Lelkola",
    LPÄ: "Lempäälä",
    LEN: "Lentoasema",
    LK: "Leppäkoski",
    LPV: "Leppävaara",
    LTS: "Leteensuo",
    LIS: "Lieksa",
    LTK: "Lieksan teollisuuskylä",
    LLH: "Lielahti",
    LVT: "Lievestuore",
    LKA: "Liminka",
    LMP: "Liminpuro",
    LUO: "Lohiluoma",
    LO: "Lohja",
    LM: "Loimaa",
    LOH: "Louhela",
    LOL: "Loukolampi",
    LVS: "Loviisan satama",
    LUI: "Luikonlahti",
    LUS: "Lusto",
    LÄ: "Luumäki",
    LHS: "Lähessuo",
    LÄP: "Länkipohja",
    MLK: "Maanselkä",
    MRI: "Maaria",
    MD: "Madesjärvi",
    MJJ: "Majajärvi",
    MKN: "Maksniemi",
    ML: "Malmi",
    MLO: "Malminkartano",
    MKA: "Mankala",
    MRK: "Markkala",
    MRL: "Martinlaakso",
    MAS: "Masala",
    MTV: "Matkaneva",
    MAT: "Mattila",
    MLL: "Melalahti",
    MSÄ: "Metsäkansa",
    MI: "Mikkeli",
    MIS: "Misi",
    MLA: "Mommila",
    MVA: "Moskova_(Leningradski)",
    MH: "Muhos",
    MUK: "Mukkula",
    MUR: "Murtomäki",
    MSO: "Mustio",
    MST: "Mustolan satama",
    MKO: "Muukko",
    MUU: "Muurame",
    MUL: "Muurola",
    MYS: "Myllykangas",
    MKI: "Myllykoski",
    MY: "Myllymäki",
    MYL: "Myllyoja",
    MYT: "Mynttilä",
    MYN: "Mynämäki",
    MYR: "Myyrmäki",
    MÄK: "Mäkkylä",
    MLÄ: "Mäntsälä",
    MÄN: "Mänttä",
    MR: "Mäntyharju",
    MN: "Mäntyluoto",
    MN_V: "Mäntyluoto erkanemisvaihde",
    NNL: "Naantali",
    NRI: "Naarajärvi",
    NAL: "Nakkila",
    NSL: "Nastola",
    NMP: "Niemenpää",
    NII: "Niinimaa",
    NMÄ: "Niinimäki",
    NNS: "Niinisalo",
    NRL: "Niirala",
    NRLR: "Niirala-raja",
    NTH: "Niittylahti",
    NLÄ: "Nikkilä",
    NSK: "Niska",
    NVL: "Nivala",
    NOA: "Nokia",
    NM: "Nummela",
    NUA: "Nuojua",
    NRM: "Nurmes",
    NÄR: "Närpiö",
    OHM: "Ohenmäki",
    OI: "Oitti",
    OLLI: "Olli",
    ONT: "Onttola",
    OM: "Orimattila",
    OV: "Orivesi",
    OVK: "Orivesi keskusta",
    OTM: "Otanmäki",
    OT: "Otava",
    OU: "Oulainen",
    OL: "Oulu",
    NOK: "Oulu Nokela",
    ORI: "Oulu Oritkari",
    OLT: "Oulu tavara",
    TUA: "Oulu Tuira",
    OLK: "Oulunkylä",
    OLL: "Oulunlahti",
    PTI: "Paimenportti",
    PO: "Paimio",
    PLP: "Palopuro",
    PTO: "Paltamo",
    PAS: "Pankakoski",
    PAR: "Parikkala",
    PKO: "Parkano",
    PRL: "Parola",
    PSL: "Pasila",
    PAU: "Pasila autojuna-asema",
    PSLT: "Pasila tavara",
    PTG: "Patokangas",
    PA: "Pelkola",
    PEL: "Pello",
    PMI: "Peltosalmi",
    PSJ: "Peräseinäjoki",
    PSK: "Pesiökylä",
    PRK: "Petroskoi / Petrozavodsk",
    PVI: "Petäjävesi",
    PM: "Pieksämäki",
    PMLA: "Pieksämäki lajittelu",
    PMT: "Pieksämäki tavara",
    TMU: "Pieksämäki Temu",
    PTR: "Pietari_(Finljandski)",
    PTL: "Pietari_(Ladozhski)",
    PTS: "Pietarsaari",
    PH: "Pihlajavesi",
    PP: "Pihtipudas",
    PIK: "Piikkiö",
    PKL: "Pikkarala",
    PIO: "Pitkäkallio",
    PTK: "Pitkämäki",
    PJM: "Pitäjänmäki",
    PKU: "Pohjankuru",
    POH: "Pohjois-Haaga",
    PLU: "Pohjois-Louko",
    PKK: "Poikkeus",
    POI: "Poiksilta",
    PRI: "Pori",
    PRV: "Porvoo",
    PUS: "Puhos",
    PLA: "Puistola",
    PMK: "Pukinmäki",
    PL: "Pulsa",
    PUN: "Punkaharju",
    PUR: "Purola",
    PYK: "Pyhäkumpu",
    PYE: "Pyhäkumpu erkanemisvaihde",
    PHÄ: "Pyhäsalmi",
    PNÄ: "Pännäinen",
    R855: "Pännäinen (R855)",
    PKY: "Pääskylahti",
    RHE: "Raahe",
    RPO: "Raippo",
    RAI: "Raisio",
    RM: "Rajamäki",
    RJP: "Rajaperkiö",
    RMI: "Rantasalmi",
    RAS: "Rasinsuo",
    RLÄ: "Ratikylä",
    RAH: "Rauha",
    RHL: "Rauhalahti",
    RMA: "Rauma",
    RIO: "Raunio",
    RAT: "Rautaruukki",
    RJÄ: "Rautjärvi",
    RPH: "Rautpohja",
    RKL: "Rekola",
    REE: "Retretti",
    R060: "Riihimäki (R060)",
    R702: "Riihimäki (R702)",
    ARP: "Riihimäki Arolampi",
    RI: "Riihimäki",
    RILA: "Riihimäki lajittelu",
    RIT: "Riihimäki tavara",
    RJR: "Riijärvi",
    RPA: "Riippa",
    RST: "Ristiina",
    RJV: "Ristijärvi",
    ROI: "Rovaniemi",
    RHA: "Ruha",
    RNN: "Runni",
    RSU: "Ruokosuo",
    RKI: "Ruukki",
    RSM: "Ruusumäki",
    RY: "Ryttylä",
    RÖY: "Röyttä",
    SAA: "Saakoski",
    SR: "Saari",
    SRJ: "Saarijärvi",
    SLN: "Salminen",
    SLO: "Salo",
    SAM: "Sammalisto",
    STA: "Santala",
    SAU: "Saunakallio",
    SMÄ: "Saunamäki",
    SAV: "Savio",
    SL: "Savonlinna",
    SK: "Seinäjoki",
    RLA: "Seinäjoki Rahkola",
    SKT: "Seinäjoki tavara",
    SPÄ: "Selänpää",
    SPJ: "Sieppijärvi",
    SVI: "Sievi",
    SKÄ: "Siikamäki",
    SIJ: "Siilinjärvi",
    SIM: "Simo",
    SPL: "Simpele",
    SIP: "Sipilä",
    STÖ: "Sisättö",
    STI: "Siuntio",
    SIU: "Siuro",
    SGY: "Skogby",
    SLD: "Sköldvik",
    SOA: "Soinlahti",
    SOR: "Sorsasalo",
    SKV: "Sukeva",
    SUO: "Suolahti",
    SNJ: "Suonenjoki",
    SNM: "Suoniemi",
    STG: "Svetogorsk",
    SYR: "Syrjä",
    SKI: "Syrjämäki",
    SMJ: "Sysmäjärvi",
    SÄ: "Säkäniemi",
    SKM: "Sänkimäki",
    SJ: "Sääksjärvi",
    TA: "Taavetti",
    TKO: "Tahkoluoto",
    TE: "Taipale",
    TV: "Talviainen",
    TLV: "Talvivaara",
    TMS: "Tammisaari",
    TPE: "Tampere",
    JVS: "Tampere Järvensivu",
    TPET: "Tampere tavara",
    VKA: "Tampere Viinikka",
    TNA: "Tapanila",
    TAP: "Tapavainola",
    TSL: "Tavastila",
    TK: "Tervajoki",
    TRV: "Tervola",
    TSO: "Tesoma",
    TUV: "Teuva",
    TKK: "Tikkala",
    TKP: "Tikkaperä",
    TKL: "Tikkurila",
    TOH: "Tohmajärvi",
    TL: "Toijala",
    TOI: "Toivala",
    TOL: "Tolsa",
    TOM: "Tommola",
    TRK: "Torkkeli",
    R742: "Tornio (R742)",
    TOR: "Tornio",
    TRI: "Tornio-Itäinen",
    TRR: "Tornio-raja",
    TRL: "Tuomarila",
    TJA: "Tuomioja",
    R530: "Tuomioja (R530)",
    TU: "Turenki",
    TKU: "Turku",
    TUS: "Turku satama",
    TKUT: "Turku tavara",
    TPV: "Tuupovaara",
    TUU: "Tuuri",
    TVE: "Tver",
    TÖR: "Törmä",
    TRÄ: "Törölä",
    UIM: "Uimaharju",
    UR: "Urjala",
    UTJ: "Utajärvi",
    UTI: "Utti",
    UKP: "Uusikaupunki",
    UKÄ: "Uusikylä",
    VKO: "Vaajakoski",
    VAA: "Vaala",
    VRA: "Vaarala",
    VS: "Vaasa",
    VJR: "Vahojärvi",
    VNA: "Vainikkala",
    VNAT: "Vainikkala tavara",
    VNAR: "Vainikkala-raja",
    VMO: "Valimo",
    VI: "Valkeakoski",
    VSO: "Valkeasuo",
    VLM: "Valtimo",
    VMA: "Vammala",
    VTR: "Vanattara",
    VKS: "Vantaankoski",
    VAR: "Varkaus",
    VUS: "Vartius",
    VUR: "Vartius-raja",
    VKH: "Vasikkahaka",
    VSK: "Vaskiluoto",
    VEH: "Vehkala",
    VKI: "Venetmäki",
    VN: "Vesanka",
    VK: "Viekki",
    VRM: "Vierumäki",
    VTI: "Vihanti",
    VIH: "Vihtari",
    VRI: "Vihtavuori",
    VIA: "Viiala",
    VNJ: "Viinijärvi",
    VYB: "Viipuri",
    VLH: "Villähde",
    VLP: "Vilppula",
    VIN: "Vinnilä",
    VMS: "Virkamies",
    VT: "Voltti",
    VHJ: "Vuohijärvi",
    VJO: "Vuojoki",
    VKT: "Vuokatti",
    VSL: "Vuonislahti",
    VNS: "Vuonos",
    VSA: "Vuosaari",
    VRS: "Värtsilä",
    YKST: "Ykspihlaja tavara",
    YKSV: "Ykspihlaja väliratapiha",
    YST: "Ylistaro",
    YTR: "Ylitornio",
    YLV: "Ylivalli",
    YV: "Ylivieska",
    YLK: "Yläkoski",
    YLY: "Ylämylly",
    YLÖ: "Ylöjärvi",
    YPY: "Ypykkävaara",
    ÄS: "Äetsä",
    ÄHT: "Ähtäri",
    ÄM: "Ämmänsaari",
    ÄKI: "Äänekoski",
  };

  const handleLongPress = (stationName: string) => {
    // Show confirmation dialog
    const confirmRemoval = window.confirm(
      `Haluatko poistaa aseman ${stationName} listalta?`,
    );

    if (confirmRemoval) {
      // Remove station from the list
      const updatedStations = previousStations.filter(
        (station) => station !== stationName,
      );

      // Update localStorage if necessary
      localStorage.setItem("lastStations", JSON.stringify(updatedStations));

      // Update state with the new list
      setPreviousStations(updatedStations.reverse());

      // Optionally reload the page or navigate
      navigate(`/set-train-number`); // Navigate to home after removal
    }
  };

  const handleMouseDown = (stationName: string) => {
    // Start the long press timer
    timerRef.current = setTimeout(() => handleLongPress(stationName), 500); // 500ms threshold
  };

  const handleMouseUp = () => {
    // Clear the timer if mouse is released before the long press
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null; // Reset the ref
    }
  };

  return (
    <div
      className="ms-5 mt-1"
      style={{ color: "white", textAlign: "start", marginLeft: 20 }}
    >
      {/* sivut yhdistetty joten takaisin nappia ei tarvitse näkyä */}
      {/* <div
        onClick={() => navigate("/set-train-number")}
        style={{
          position: "absolute",
          top: 44,
          left: 0,
          margin: "10px",
          padding: "5px 10px",
          backgroundColor: "#1e5a72",
          color: "white",
          border: "none",
          borderRadius: 8,
          cursor: "pointer",
          boxShadow: "12px 2px 25px rgba(0, 0, 0, 0.4)",
        }}
      >
        ←
      </div> */}
      <div className="yla-osa">
        <div
          style={{
            width: "96%",
            height: 1,
            backgroundColor: "transparent",
            margin: 14,
          }}
        ></div>

        <h1
          style={{
            marginLeft: 36,
            marginBottom: 2,
            fontSize: 22,
            fontWeight: 900,
          }}
        >
          Lähtevät Junat
        </h1>
        <input
          type="text"
          style={{
            marginLeft: 32,
            width: "220px",
            borderRadius: 4,
            boxShadow: "2px 2px 25px rgba(0, 0, 0, 0.9)",
          }}
          placeholder="asema"
          onChange={(e) => handleInputStation(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              (e.target as HTMLInputElement).blur();
            }
          }}
          // autoFocus
        />
      </div>
      <div
        className="edelliset-junat"
        style={{
          display: "flex",
          flexWrap: "wrap",
          marginTop: "10px",
          marginLeft: "20px",
          paddingBottom: "0px",
        }}
      >
        {previousStations.length > 0 ? (
          previousStations.map((station, index) => (
            <div
              key={index}
              onClick={() => setNewStation(station)}
              onMouseDown={() => handleMouseDown(station)}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={() => handleMouseDown(station)}
              onTouchEnd={handleMouseUp}
              style={{
                width: "fit-content",
                height: "34px",
                padding: 8,
                backgroundColor: "rgba(30, 90, 114, 0.4)",
                color: "lightgray",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "6px",
                borderRadius: "2px",
                border: "none",
                cursor: "pointer", // Change cursor to pointer
                boxShadow: "2px 2px 12px rgba(0, 0, 0, 0.6)",
              }}
            >
              {station} {/* Display the station name or train number */}
            </div>
          ))
        ) : (
          <div></div>
        )}
      </div>
      <br />
      <div
        className="asema-lahdot"
        style={{
          width: "100%",
          minHeight: "100vh",
          position: "absolute",
          left: 0,
          padding: "8px 0 8px 0",
          backgroundColor: "#032938",
          borderTop: "1px solid gray",
          zIndex: 2,
        }}
      >
        <div className="ms-4">
          {newStation && (
            <div className="d-flex">
              <h2 className="mb-4">{stationNames[newStation]}</h2>
              {!showMore && stationTrains[newStation] && (
                <button
                  onClick={() => setShowMore(true)}
                  style={{
                    marginLeft: 20,
                    marginBottom: "24px",
                    padding: "4px 8px",
                    backgroundColor: "#1e5a72",
                    color: "white",
                    border: "none",
                    borderRadius: 8,
                    cursor: "pointer",
                    boxShadow: "12px 2px 25px rgba(0, 0, 0, 0.4)",
                  }}
                >
                  Näytä seuraavat 24h
                </button>
              )}
            </div>
          )}
          {loading ? (
            <div className="loader"></div>
          ) : (
            <div
              className="vaihtoyhteydet"
              style={{
                width: "100%",
                color: "white",
                position: "absolute",
                left: 0,
                fontSize: 12,
                marginTop: "-8px",
                padding: "8px 0 8px 0",
                textAlign: "left",
                backgroundColor: "#032938",
              }}
            >
              {stationTrains[newStation] ? (
                <>
                  {/* Kaukojunat */}
                  {stationTrains[newStation]
                    .filter((train) => {
                      if (showMore) {
                        return (
                          train.trainType !== "HL" && train.trainType !== "HLV"
                        );
                      }
                      const scheduledTime = new Date(
                        train.scheduledTime,
                      ).getTime();
                      const currentTime = Date.now();
                      const oneHourInMs = 2 * 60 * 60 * 1000;
                      return (
                        train.trainType !== "HL" &&
                        train.trainType !== "HLV" &&
                        scheduledTime >= currentTime &&
                        scheduledTime <= currentTime + oneHourInMs
                      );
                    })
                    .sort((a, b) => {
                      const timeA = new Date(a.scheduledTime).getTime();
                      const timeB = new Date(b.scheduledTime).getTime();
                      return timeA - timeB;
                    })
                    .map((train, index) => (
                      <div
                        key={index}
                        className="vaihtoasema-info-item-departures d-flex"
                        style={{
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          margin: 4,
                          fontSize: 16,
                        }}
                      >
                        <strong
                          onClick={() =>
                            handleClickTrain(
                              train.trainType,
                              train.trainNumber,
                              train.destinationStation,
                            )
                          }
                          style={{
                            width: 80,
                            height: "fit-content",
                            textAlign: "right",
                            marginRight: 6,
                            whiteSpace: "nowrap",
                            cursor: "pointer",
                            padding: "0px 4px",
                            backgroundColor: "rgba(30, 90, 114, 0.4)",
                            color: "white",
                            border: "none",
                            borderRadius: 4,
                            boxShadow: "1px 2px 2px rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          {train.trainType === "HL" || train.trainType === "HLV"
                            ? train.commuterLineID
                            : `${train.trainType} ${train.trainNumber}`}
                        </strong>
                        <div className="vaihtoasema-info-item-departures">
                          {stationNames[train.destinationStation]}
                        </div>

                        {train.cancelled ? (
                          <div
                            className="vaihtoasema-info-item-departures"
                            style={{
                              color: "rgb(211, 130, 146)",
                            }}
                          >
                            <strong>Peruttu</strong>
                          </div>
                        ) : (
                          <>
                            <div className="raide-circle me-1">
                              {train.commercialTrack || "N/A"}
                            </div>
                            <div className="me-1">
                              {new Date(train.scheduledTime).toLocaleTimeString(
                                "fi-FI",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </div>
                            {new Date(train.liveEstimateTime).getTime() -
                              new Date(train.scheduledTime).getTime() >
                              120000 && (
                              <div
                                className=""
                                style={{
                                  color: "rgb(211, 130, 146)",
                                }}
                              >
                                {new Date(
                                  train.liveEstimateTime,
                                ).toLocaleTimeString("fi-FI", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}

                  {/* Lähijunat */}
                  <div className="m-4"></div>
                  {stationTrains[newStation]
                    .filter((train) => {
                      if (showMore) {
                        return (
                          train.trainType === "HL" || train.trainType === "HLV"
                        );
                      }
                      const scheduledTimeLahi = new Date(
                        train.scheduledTime,
                      ).getTime();
                      const currentTimeLahi = Date.now();
                      const oneHourInMsLahi = 2 * 60 * 60 * 1000;
                      return (
                        (train.trainType === "HL" ||
                          train.trainType === "HLV") &&
                        scheduledTimeLahi >= currentTimeLahi &&
                        scheduledTimeLahi <= currentTimeLahi + oneHourInMsLahi
                      );
                    })
                    .sort((a, b) => {
                      const timeA = new Date(a.scheduledTime).getTime();
                      const timeB = new Date(b.scheduledTime).getTime();
                      return timeA - timeB;
                    })
                    .map((train, index) => (
                      <div
                        key={index}
                        className="vaihtoasema-info d-flex"
                        style={{
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          margin: 4,
                          fontSize: 16,
                        }}
                      >
                        <strong
                          onClick={() =>
                            handleClickTrain(
                              train.commuterLineID,
                              train.trainNumber,
                              train.destinationStation,
                            )
                          }
                          style={{
                            width: 44,
                            height: "fit-content",
                            textAlign: "right",
                            marginRight: 12,
                            whiteSpace: "nowrap",
                            cursor: "pointer",
                            padding: "0px 10px",
                            backgroundColor: "rgba(30, 90, 114, 0.4)",
                            color: "white",
                            border: "none",
                            borderRadius: 4,
                            boxShadow: "1px 2px 2px rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          {train.trainType === "HL" || train.trainType === "HLV"
                            ? train.commuterLineID
                            : `${train.trainType} ${train.trainNumber}`}
                        </strong>
                        <div className="vaihtoasema-info-item-departures">
                          {stationNames[train.destinationStation]}
                        </div>

                        {train.cancelled ? (
                          <div
                            className="vaihtoasema-info-item-departures"
                            style={{
                              color: "rgb(211, 130, 146)",
                            }}
                          >
                            <strong>Peruttu</strong>
                          </div>
                        ) : (
                          <>
                            <div className="raide-circle me-1">
                              {train.commercialTrack || "N/A"}
                            </div>
                            <div className="vaihtoasema-info-item-departures">
                              {new Date(train.scheduledTime).toLocaleTimeString(
                                "fi-FI",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                </>
              ) : (
                <div></div>
              )}
              <div
                style={{
                  height: 240,
                  backgroundColor: "transparent",
                }}
              ></div>
            </div>
          )}
        </div>
        {showConfirm && (
          <CustomConfirm
            message={`Haluatko siirtyä junan ${moveToTrain} aikatauluun?`}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
          />
        )}
      </div>

      <nav
        style={{
          padding: "4px",
          backgroundColor: "#1e5a72",
          color: "lightgray",
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          textAlign: "center",
          fontFamily: "Arial, sans-serif",
          fontSize: 11,
          zIndex: 2,
        }}
      >
        <div
          className="d-flex justify-content-center"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            maxWidth: "800px",
            margin: "0 auto",
            gap: "15px",
          }}
        >
          <Link to="/set-train-number" className="nav-link">
            minunjunani.fi
          </Link>
          <Link to="/privacy-policy" className="nav-link">
            Tietosuojakäytäntö
          </Link>
          <Link to="/cookies" className="nav-link">
            Evästeet
          </Link>
        </div>
        <div className="">&copy; {year} minunjunani.fi</div>
        <a
          href="https://www.digitraffic.fi/kayttoehdot/"
          target="_blank"
          style={{ color: "darkgray" }}
        >
          Liikennetietojen lähde Fintraffic / digitraffic.fi, lisenssi CC 4.0 BY
        </a>
      </nav>
    </div>
  );
};

export default StationDepartures;
